/**
 * Minimal 3D vector algebra for the simulation.
 *
 * Sim cannot import three.js, so it carries its own vectors. Values are plain immutable objects,
 * which keeps sim state serializable and cheap to snapshot. Render converts to `Vector3` at the
 * boundary.
 *
 * World axes: +X right, +Y up, -Z downrange. The shooter looks toward -Z at yaw 0.
 */

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const ZERO: Vec3 = { x: 0, y: 0, z: 0 };
export const UP: Vec3 = { x: 0, y: 1, z: 0 };
export const DOWNRANGE: Vec3 = { x: 0, y: 0, z: -1 };

export function vec3(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function scale(v: Vec3, factor: number): Vec3 {
  return { x: v.x * factor, y: v.y * factor, z: v.z * factor };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function lengthSquared(v: Vec3): number {
  return dot(v, v);
}

export function length(v: Vec3): number {
  return Math.sqrt(lengthSquared(v));
}

/** Returns the unit vector. Throws `RangeError` on a zero-length input. */
export function normalize(v: Vec3): Vec3 {
  const magnitude = length(v);
  if (magnitude === 0) {
    throw new RangeError('normalize: cannot normalize a zero-length vector');
  }
  return scale(v, 1 / magnitude);
}

export interface Basis {
  readonly right: Vec3;
  readonly up: Vec3;
}

/**
 * Builds an orthonormal pair perpendicular to `forward`.
 *
 * Crossing with world up collapses when aiming straight up or down, so a near-vertical forward
 * falls back to the downrange axis as its reference.
 */
export function basisFrom(forward: Vec3): Basis {
  const reference = Math.abs(forward.y) > 0.9999 ? DOWNRANGE : UP;
  const right = normalize(cross(forward, reference));
  return { right, up: cross(right, forward) };
}

/**
 * Unit direction for a yaw and pitch pair, in radians. Yaw 0 with pitch 0 looks downrange, positive
 * pitch looks up, positive yaw turns left.
 */
export function directionFromAngles(yaw: number, pitch: number): Vec3 {
  const cosPitch = Math.cos(pitch);
  return {
    x: -Math.sin(yaw) * cosPitch,
    y: Math.sin(pitch),
    z: -Math.cos(yaw) * cosPitch,
  };
}

/**
 * Turns a vector about the world up axis by `angle` radians. The inverse is the negated angle,
 * which is what puts a ray into the local frame of something that is facing a direction.
 */
export function rotateY(v: Vec3, angle: number): Vec3 {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: v.x * cos + v.z * sin, y: v.y, z: -v.x * sin + v.z * cos };
}

/**
 * Leans a vector about the world X axis by `angle` radians. Positive raises the -Z end, which is
 * the convention a tilted collider is authored in: `rampCollider` builds its slope from it.
 *
 * A solid's orientation is `rotateY(rotateX(v, tilt), yaw)`, so the inverse that puts a world point
 * into its local frame is `rotateX(rotateY(p, -yaw), -tilt)`, undoing yaw first.
 */
export function rotateX(v: Vec3, angle: number): Vec3 {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: v.x, y: v.y * cos - v.z * sin, z: v.y * sin + v.z * cos };
}

/** Component-wise linear blend, with `t` unclamped so callers control extrapolation. */
export function mix(a: Vec3, b: Vec3, t: number): Vec3 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}
